import { blindsIndexParser, currencyIndexParser, gameSpeedIndexParser } from '../../../../types/pokerTypes';
import { lobbyPokerVariantIndexParser } from '../../../../types/pokerVariantTypes';
import { optionalPlayerAvatarParser } from '../../commonInbounds/utils/playerAvatarInboundUtils';
import { parseInboundPayload } from '../../inbound';
import { booleanParser, encodedStringParser, numberParser, stringParser } from '../../inboundParsers';
import { splitWithBraces, trimBraces } from '../../inboundUtils';
function roomSummaryHtmlParser(str) {
    return encodedStringParser(trimBraces(str));
}
function lobbyRoomDisplayTypeParser(str) {
    // map array index to the ordinal value of the corresponding room display type:
    const lobbyRoomDisplayTypeArray = [
        'game',
        'tourney',
        'chat',
        'mtt_staging',
        'mtt_room',
        'mtt_staging_running',
        'automtt_staging',
        'automtt_staging_running',
        'level_tourney',
        'jacks_sponsored_tourney',
        'jacks_sponsored_mtt_staging',
        'jacks_sponsored_mtt_staging_running',
        'all_in'
    ];
    return lobbyRoomDisplayTypeArray[Number(str)];
}
function lobbyRoomAvailabilityDisplayTypeParser(str) {
    // map array index (ordinal / enum value) to the game availability display type:
    const lobbyRoomAvailabilityDisplayTypes = [
        'open',
        'dimmed',
        'open-for-spectators'
    ];
    return lobbyRoomAvailabilityDisplayTypes[Number(str)];
}
const LOBBY_ROOM_PART_DEFINITIONS = [
    { name: 'roomId', parser: numberParser },
    { name: 'roomVarsString', parser: stringParser }, // TODO: parse full object
    { name: 'roomName', parser: encodedStringParser },
    { name: 'lobbyId', parser: numberParser },
    { name: 'roomDisplayType', parser: lobbyRoomDisplayTypeParser },
    { name: 'roomSummaryHtml', parser: roomSummaryHtmlParser },
    { name: 'playersString', parser: encodedStringParser },
    { name: 'numPlayers', parser: numberParser },
    { name: 'numPlayersTotal', parser: numberParser },
    { name: 'blindsLevel', parser: blindsIndexParser },
    { name: 'buyin', parser: numberParser },
    { name: 'currency', parser: currencyIndexParser },
    { name: 'isTourneyTable', parser: booleanParser },
    { name: 'isLockedTourney', parser: booleanParser },
    { name: 'isDualLimitRoom', parser: booleanParser },
    { name: 'lobbyPokerVariant', parser: lobbyPokerVariantIndexParser },
    { name: 'gameSpeed', parser: gameSpeedIndexParser },
    { name: 'sidebetPhaseLabel', parser: stringParser }, // TODO: is this an enum?
    { name: 'isPowerPlayersOnly', parser: booleanParser },
    { name: 'gameSettingsString', parser: stringParser }, // TODO: parse full object AND HTML
    { name: 'lobbyRoomAvailabilityDisplayType', parser: lobbyRoomAvailabilityDisplayTypeParser },
    { name: 'sponsorAvatar', parser: optionalPlayerAvatarParser },
    { name: 'sponsorTooltipLabel', parser: encodedStringParser },
    { name: 'isAggroBettingFilterEnabled', parser: booleanParser },
    { name: 'numSeats', parser: numberParser },
    { name: 'hasBuyInRange', parser: booleanParser },
    { name: 'minBuyIn', parser: numberParser },
    { name: 'maxBuyIn', parser: numberParser }
];
export const lobbyRoomParser = (str) => {
    const inboundLobbyRoom = parseInboundPayload(splitWithBraces(trimBraces(str)), LOBBY_ROOM_PART_DEFINITIONS);
    return getLobbyRoomObjectFromInbound(inboundLobbyRoom);
};
export function getLobbyRoomObjectFromInbound(inboundLobbyRoom) {
    /**
     * TODO: hacky, matches on:
     * 'mtt_staging'
     * 'mtt_room'
     * 'mtt_staging_running'
     * 'automtt_staging'
     * 'automtt_staging_running'
     * 'jacks_sponsored_mtt_staging'
     * 'jacks_sponsored_mtt_staging_running'
     */
    const isMtt = inboundLobbyRoom.roomDisplayType.indexOf('mtt_') !== -1;
    const isAutoMtt = isMtt && inboundLobbyRoom.roomDisplayType.indexOf('auto') !== -1;
    // roomVarsString may be in the form: `{{has_open_seat,1},{players_sitting,-1}}`
    const roomVars = splitWithBraces(trimBraces(inboundLobbyRoom.roomVarsString)).reduce((acc, roomVarString) => {
        const [roomVarName, roomVarValueString] = trimBraces(roomVarString).split(',');
        switch (roomVarName) {
            case 'has_open_seat': {
                acc.hasOpenSeat = booleanParser(roomVarValueString);
                break;
            }
            case 'players_sitting': {
                // apparently unused in Flash client
                const numPlayersSitting = numberParser(roomVarValueString);
                if (numPlayersSitting !== -1) {
                    acc.numPlayersSitting = numPlayersSitting;
                }
                break;
            }
            default: {
                console.warn(`Unrecognized lobby room var ${roomVarString}`);
            }
        }
        return acc;
    }, {});
    return {
        isMtt,
        isAutoMtt,
        roomId: inboundLobbyRoom.roomId,
        roomName: inboundLobbyRoom.roomName,
        roomDisplayType: inboundLobbyRoom.roomDisplayType,
        roomSummaryHtml: inboundLobbyRoom.roomSummaryHtml,
        playersString: inboundLobbyRoom.playersString,
        numPlayers: inboundLobbyRoom.numPlayers,
        numPlayersTotal: inboundLobbyRoom.numPlayersTotal,
        buyin: inboundLobbyRoom.buyin,
        currency: inboundLobbyRoom.currency,
        isTourneyTable: inboundLobbyRoom.isTourneyTable,
        isLockedTourney: inboundLobbyRoom.isLockedTourney,
        isDualLimitRoom: inboundLobbyRoom.isDualLimitRoom,
        lobbyPokerVariant: inboundLobbyRoom.lobbyPokerVariant,
        gameSpeed: inboundLobbyRoom.gameSpeed,
        isPowerPlayersOnly: inboundLobbyRoom.isPowerPlayersOnly,
        gameSettingsString: inboundLobbyRoom.gameSettingsString,
        lobbyRoomAvailabilityDisplayType: inboundLobbyRoom.lobbyRoomAvailabilityDisplayType,
        sponsorAvatar: inboundLobbyRoom.sponsorAvatar,
        isAggroBettingFilterEnabled: inboundLobbyRoom.isAggroBettingFilterEnabled,
        numSeats: inboundLobbyRoom.numSeats,
        hasBuyInRange: inboundLobbyRoom.hasBuyInRange,
        minBuyIn: inboundLobbyRoom.minBuyIn,
        maxBuyIn: inboundLobbyRoom.maxBuyIn,
        ...roomVars
    };
}
